import { UseMutationResult } from "@tanstack/react-query";
import { cloneDeep, isEqual } from "lodash";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { UpdateAddresses } from "../../../api/routes";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { SovColumnProps } from "./AddressCardColumns";
import styles from "./ColumnEditModal.module.css";
import { RawAddressWithIndices } from "./types";

type ColumnEditModalProps = {
  columnInfo: SovColumnProps | null;
  visible: boolean;
  fullAddresses: RawAddressWithIndices[];
  onHide: () => void;
  setVisible: (visible: boolean) => void;
  // Better to do this or just mint it? I know this has come up in the past...
  updateAddresses: UseMutationResult<
    client.UpdateAddressResponse,
    Error,
    UpdateAddresses,
    unknown
  >;
  onSaved: () => void;
};

export const ColumnEditModal: React.FC<ColumnEditModalProps> = ({
  columnInfo,
  visible,
  fullAddresses,
  onHide,
  setVisible,
  updateAddresses,
  onSaved,
}) => {
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>([]);
  const [newValue, setNewValue] = useState<string | number>("");
  const toast = useToast();

  useEffect(() => {
    if (!visible) {
      setSelectedValues([]);
      setNewValue("");
    }
  }, [visible]);

  const workingField = columnInfo?.field;
  if (!columnInfo || !workingField) {
    return <></>;
  }

  const groupedValues = Object.groupBy(
    fullAddresses,
    (address) => columnInfo.getValue(address) || "Blank"
  );

  // Calculate total addresses to update based on selected values
  const totalAddressesToUpdate = selectedValues.reduce<number>(
    (sum, name) => sum + (groupedValues[name] ?? []).length,
    0
  );

  const addressesToUpdate = async (): Promise<client.Address[]> => {
    const addresses: client.Address[] = [];
    fullAddresses.forEach((fullAddress) => {
      // We are only using this to determine membership in selectedValues, not
      // for the actual update:
      const addressValue = columnInfo.getValue(fullAddress) || "Blank";
      // const strValue = String(columnInfo.getValue(fullAddress) || "Blank");
      if (selectedValues.includes(addressValue)) {
        const addressToUpdate = cloneDeep(fullAddress.__extras.fullAddress);
        if (
          isEqual(
            newValue,
            addressToUpdate.private_computed_values[workingField]
          )
        ) {
          delete addressToUpdate.private_overrides[workingField];
        } else {
          addressToUpdate.private_overrides[workingField] = newValue;
        }
        addresses.push(addressToUpdate);
      }
    });
    return addresses;
  };

  const handleSave = async () => {
    try {
      const addresses = await addressesToUpdate();
      await updateAddresses.mutateAsync({ addresses: addresses });
      onSaved();
    } catch (err) {
      toast.show({
        severity: "error",
        summary: "Failed to update addresses",
        sticky: true,
        icon: "pi pi-times",
      });
    }
  };

  // Detect if all addresses are US
  const isUnitedStates = fullAddresses.every(
    (addr) => addr.country_code === "US"
  );

  // Use modalEditor if available
  let editorNode: React.ReactNode = null;
  if (columnInfo.editor) {
    const ColumnModalEditor = columnInfo.editor;
    // Only pass countryCode if the editor supports it
    editorNode = (
      <ColumnModalEditor
        options={{ value: newValue, editorCallback: setNewValue }}
        disabled={updateAddresses.isPending}
        isUnitedStates={isUnitedStates}
        fieldType={columnInfo.field === "tiv" ? "number" : "string"}
        additionalStyles={{}}
      />
    );
  }

  return (
    <Dialog
      header={`Bulk Edit ${columnInfo.columnName}`}
      className={styles.dialog}
      visible={visible}
      onHide={() => setVisible(false)}
      footer={
        <div className={styles.dialogFooter}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            severity="secondary"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            onClick={handleSave}
            disabled={totalAddressesToUpdate === 0}
            loading={updateAddresses.isPending}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.pairedLabelAndBox}>
          <label htmlFor="bulk-multiselect" className={styles.multiSelectLabel}>
            <strong>Search</strong>
          </label>
          <MultiSelect
            value={selectedValues}
            scrollHeight="400px"
            onChange={(e) => setSelectedValues(e.value)}
            options={Object.entries(groupedValues).map(([name, addresses]) => ({
              name: name,
              count: (addresses ?? []).length,
            }))}
            className={styles.multiSelect}
            panelClassName={styles.multiSelectPanel}
            maxSelectedLabels={4}
            filter
            placeholder="Matching values to update"
            optionLabel="name"
            optionValue="name"
            display="chip"
            inputId="bulk-multiselect"
            itemTemplate={(item) => {
              return (
                <div>
                  <div className={styles.multiSelectItemTemplateName}>
                    {item.name}
                  </div>
                  <div className={styles.multiSelectItemTemplateSummary}>
                    {item.count} addresses impacted
                  </div>
                </div>
              );
            }}
          />
        </div>
        <div className={styles.pairedLabelAndBox}>
          <label htmlFor="bulk-replace" className={styles.multiSelectLabel}>
            <strong>Replace with</strong>
          </label>
          {editorNode}
        </div>
        {totalAddressesToUpdate > 0 && (
          <div>
            <Message
              // Consider changing to info severity if the number of addresses is small
              severity="warn"
              text={`This will update ${totalAddressesToUpdate} addresses`}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
